import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  apiPost,
  type SignalExtractionBatchPayload,
  type SignalExtractionPayload,
} from "@/lib/api";

interface ExtractInboxItemsVars {
  inbox_item_ids: string[];
}

interface ExistingInboxBatchVars {
  inbox_item_ids?: string[];
  source_id?: string[];
  item_type?: string[];
  status?: string;
  symbol?: string;
  since?: string;
  until?: string;
  time_basis?: "captured" | "source";
  only_unstructured?: boolean;
  force?: boolean;
  limit?: number;
  group_size?: number;
}

async function createExtraction(
  vars: ExtractInboxItemsVars,
): Promise<SignalExtractionPayload> {
  return apiPost<SignalExtractionPayload>(
    "/api/v1/inbox/extractions",
    vars,
  );
}

export function useInboxExtraction() {
  const qc = useQueryClient();
  return useMutation<
    SignalExtractionPayload,
    Error,
    ExtractInboxItemsVars,
    string | number
  >({
    mutationFn: createExtraction,
    onMutate: (vars) =>
      toast.loading(
        `Analyzing ${vars.inbox_item_ids.length} selected inbox item${vars.inbox_item_ids.length === 1 ? "" : "s"}…`,
      ),
    onSuccess: (payload) => {
      const count = payload.inbox_item_ids.length;
      toast.success(`Converted ${count} inbox item${count === 1 ? "" : "s"}`);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
    onError: (err) => {
      toast.error(`LLM conversion failed: ${err.message}`);
    },
    onSettled: (_data, _error, _vars, toastId) => {
      if (toastId !== undefined) toast.dismiss(toastId);
    },
  });
}

export function useInboxBatchExtraction() {
  const qc = useQueryClient();
  return useMutation<
    SignalExtractionBatchPayload,
    Error,
    ExistingInboxBatchVars,
    string | number
  >({
    mutationFn: (vars) =>
      apiPost<SignalExtractionBatchPayload>(
        "/api/v1/inbox/extractions/batch",
        vars,
      ),
    onMutate: (vars) =>
      toast.loading(
        vars.inbox_item_ids && vars.inbox_item_ids.length > 0
          ? `Analyzing ${vars.inbox_item_ids.length} selected inbox item${vars.inbox_item_ids.length === 1 ? "" : "s"}…`
          : "Analyzing matching inbox items…",
      ),
    onSuccess: (payload) => {
      toast.success(
        `Converted ${payload.converted_count} inbox item${payload.converted_count === 1 ? "" : "s"}`,
      );
      if (payload.failed_count > 0) {
        toast.warning(`${payload.failed_count} conversion${payload.failed_count === 1 ? "" : "s"} failed`);
      }
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
      qc.invalidateQueries({ queryKey: ["operator-status"] });
    },
    onError: (err) => {
      toast.error(`Batch conversion failed: ${err.message}`);
    },
    onSettled: (_data, _error, _vars, toastId) => {
      if (toastId !== undefined) toast.dismiss(toastId);
    },
  });
}
